// Cross-spot session finder: given my next N hours and a travel radius, which
// spot and which window? Pure ranking over per-spot windows the existing
// engine already builds (buildWindows + describeWindow), so the finder is the
// same brain pointed across the map rather than at one beach. Client-safe: no
// Supabase, no framework imports.

import type {
  PlannerSport,
  SessionHour,
  SessionPreferences,
  SessionWindow,
} from "./types";
import {
  buildHoursForSport,
  safetyContextForSport,
  type FinderData,
  type SupProfileSessionHour,
} from "./api-types";
import { prefScoringKey } from "./preferences";
import {
  buildPlan,
  buildSupProfilePlan,
  type SupProfileSessionPlan,
  type SupProfileSessionWindow,
} from "./plan";
import { isSupProfileKey } from "../conditions/sport-profiles";

/**
 * Honest crow-flies to drive-time estimate for coastal roads: a winding
 * factor on the straight line at modest average speed, plus parking faff.
 * Estimates are labelled as such in the UI ("about 25 min drive").
 */
const ROAD_WINDING_FACTOR = 1.4;
const COASTAL_AVG_KMH = 48;
const MOUNTAIN_ROAD_WINDING_FACTOR = 1.6;
const MOUNTAIN_AVG_KMH = 38;
const PARK_AND_FAFF_MIN = 5;

export function estimateDriveMinutes(
  distanceKm: number,
  terrain: "coastal" | "mountain" = "coastal",
): number {
  if (distanceKm <= 0.5) return 0; // you are basically there
  const windingFactor = terrain === "mountain" ? MOUNTAIN_ROAD_WINDING_FACTOR : ROAD_WINDING_FACTOR;
  const avgKmh = terrain === "mountain" ? MOUNTAIN_AVG_KMH : COASTAL_AVG_KMH;
  return Math.round(PARK_AND_FAFF_MIN + (distanceKm * windingFactor * 60) / avgKmh);
}

/** Minimum worthwhile time in the water after arriving (hours). */
const MIN_SESSION_H = 0.75;

/** Windows worth travelling for; the rest never leave the home spot's card. */
const RANKABLE_LABELS = new Set(["best", "good", "possible"]);

export type FinderSpot<TWindow extends SessionWindow = SessionWindow> = {
  slug: string;
  name: string;
  distanceKm: number;
  windows: TWindow[];
  /** Exact named-profile fit provenance for an honest empty Finder state. */
  fitEvidenceNote?: string | null;
};

export type RankedSession<TWindow extends SessionWindow = SessionWindow> = {
  slug: string;
  name: string;
  distanceKm: number;
  driveMinutes: number;
  window: TWindow;
  /** Realistic earliest start: window opening, or arrival time if later. */
  effectiveStartHour: number;
  /** fitScore discounted by travel; the ranking key. */
  rankScore: number;
};

export type RankOptions = {
  /** Local decimal hour now (today's reachability gate). */
  nowHour: number;
  /** "I have N hours": only today's windows reachable inside the horizon.
   * null = no horizon, both days rank. */
  horizonHours: number | null;
  /** Best window per spot only (the finder list), default true. */
  bestPerSpot?: boolean;
  maxResults?: number;
};

export type PlannerFinderWindow = SessionWindow | SupProfileSessionWindow;

/**
 * Build each candidate with the same exact Planner path used on its spot page.
 * A named profile is fail-closed unless this Finder response independently
 * carries the matching server-granted profile key.
 */
export function buildFinderSpotsForPreferences(
  data: FinderData,
  sport: PlannerSport,
  prefs: SessionPreferences,
): FinderSpot<PlannerFinderWindow>[] {
  const scoringKey = prefScoringKey(prefs);
  const namedSupProfile = isSupProfileKey(scoringKey);
  if (
    namedSupProfile &&
    !data.availableSupProfiles?.includes(scoringKey)
  ) {
    return [];
  }

  const spots: FinderSpot<PlannerFinderWindow>[] = [];
  for (const candidate of data.spots) {
    if (!candidate.availableSports.includes(sport)) continue;
    const safety = safetyContextForSport(candidate, scoringKey, sport);
    const ctx = {
      waterStatus: "unknown" as const,
      beachFacingDeg: candidate.beachFacingDeg,
      ...safety,
    };
    const plan = namedSupProfile
      ? buildSupProfilePlan({
          prefs,
          hours: buildHoursForSport(candidate, scoringKey) as SupProfileSessionHour[],
          ctx,
          waterTempC: candidate.waterTempC,
          spotName: candidate.name,
        })
      : buildPlan({
          prefs,
          hours: buildHoursForSport(candidate, scoringKey) as SessionHour[],
          ctx,
          waterTempC: candidate.waterTempC,
          spotName: candidate.name,
        });
    spots.push({
      slug: candidate.slug,
      name: candidate.name,
      distanceKm: candidate.distanceKm,
      windows: plan.windows,
      ...(namedSupProfile
        ? { fitEvidenceNote: (plan as SupProfileSessionPlan).fitEvidenceNote }
        : {}),
    });
  }
  return spots;
}

/** Travel discount: a near-perfect window an hour away should lose to a very
 * good one ten minutes away, but still beat a mediocre local option. */
function travelFactor(driveMinutes: number): number {
  return 1 - Math.min(0.35, driveMinutes / 360);
}

export function rankSessions<TWindow extends SessionWindow>(
  spots: readonly FinderSpot<TWindow>[],
  opts: RankOptions,
): RankedSession<TWindow>[] {
  const { nowHour, horizonHours } = opts;
  const maxResults = opts.maxResults ?? 6;
  const bestPerSpot = opts.bestPerSpot ?? true;

  const out: RankedSession<TWindow>[] = [];
  for (const spot of spots) {
    const driveMinutes = estimateDriveMinutes(spot.distanceKm);
    const arrivalHour = nowHour + driveMinutes / 60;
    const candidates: RankedSession<TWindow>[] = [];

    for (const window of spot.windows) {
      if (!RANKABLE_LABELS.has(window.label)) continue;

      let effectiveStartHour: number;
      if (window.dayOffset === 0) {
        effectiveStartHour = Math.max(window.startHour, arrivalHour);
        // Must leave a worthwhile session before the window shuts.
        if (effectiveStartHour > window.endHour - MIN_SESSION_H) continue;
        if (horizonHours !== null) {
          // The session must fit inside "my next N hours".
          if (effectiveStartHour + MIN_SESSION_H > nowHour + horizonHours + 1e-9) continue;
        }
      } else {
        // Tomorrow: out of scope when the user asked about the next N hours.
        if (horizonHours !== null) continue;
        effectiveStartHour = window.startHour;
      }

      candidates.push({
        slug: spot.slug,
        name: spot.name,
        distanceKm: spot.distanceKm,
        driveMinutes,
        window,
        effectiveStartHour,
        rankScore: window.fitScore * travelFactor(driveMinutes),
      });
    }

    candidates.sort((a, b) => b.rankScore - a.rankScore);
    if (bestPerSpot) {
      if (candidates[0]) out.push(candidates[0]);
    } else {
      out.push(...candidates);
    }
  }

  out.sort((a, b) => b.rankScore - a.rankScore);
  return out.slice(0, maxResults);
}

/** "Go at 16:00" formatting for an effective start (quarter-hour rounded up,
 * never before the window text suggests). */
export function goTimeLabel(effectiveStartHour: number): string {
  const quarter = Math.ceil(effectiveStartHour * 4) / 4;
  const h = Math.floor(quarter);
  const m = Math.round((quarter - h) * 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** "about 25 min drive" / "right here". */
export function driveLabel(driveMinutes: number): string {
  if (driveMinutes === 0) return "right here";
  if (driveMinutes < 60) return `about ${driveMinutes} min drive`;
  const h = Math.floor(driveMinutes / 60);
  const m = driveMinutes % 60;
  return m === 0 ? `about ${h} h drive` : `about ${h} h ${m} min drive`;
}
